<template>
  <div class="ticket-box-history-tab">
    <!-- 新增票匣區塊 -->
    <section class="mb-[20px] py-[30px] px-[24px] bg-white rounded">
      <BaseElForm
        ref="addFormRef"
        :model="formData"
        :rules="formRules"
        label-width="120px"
        label-position="left"
      >
        <BaseElFormItem :label="$t('member.tab.ticketBoxHistory.selectTicketBox')" prop="ticketBoxId">
          <BaseElSelect
            v-model="formData.ticketBoxId"
            :placeholder="$t('member.tab.ticketBoxHistory.clickToSelect')"
            popper-class="select-popper"
            @visible-change="showTicketBoxSelectModal = true"
          >
            <BaseElSelectOption
              v-if="formData.ticketBoxId"
              :value="formData.ticketBoxId"
              :label="selectedTicketBoxName"
            />
          </BaseElSelect>
        </BaseElFormItem>

        <BaseElFormItem :label="$t('member.tab.ticketBoxHistory.quantity')" prop="availableTimes">
          <BaseElInputNumber
            v-model="formData.availableTimes"
            class="quantity-input"
            :min="1"
          />
        </BaseElFormItem>

        <BaseElFormItem>
          <BaseElButton plain @click="clearForm">{{ $t('member.tab.ticketBoxHistory.clear') }}</BaseElButton>
          <BaseElButton
            type="primary"
            :loading="loading.add"
            @click="onCreateRecord"
          >
            {{ $t('member.tab.ticketBoxHistory.addTicketBox') }}
          </BaseElButton>
        </BaseElFormItem>
      </BaseElForm>
    </section>

    <!-- 票匣選擇 Modal -->
    <TicketBoxSelectModal
      v-if="showTicketBoxSelectModal"
      :ticketBoxList="ticketBoxList"
      :selectedId="formData.ticketBoxId"
      @confirm="onSelectTicketBox"
      @close="showTicketBoxSelectModal = false"
    />

    <!-- 票匣紀錄列表 -->
    <section class="bg-white rounded p-[24px]">
      <div class="filters-wrapper mb-[16px]">
        <div class="filter-row">
          <SearchTextInput :value.sync="search.code" :placeholder="$t('member.tab.ticketBoxHistory.searchCodePlaceholder')" @refresh="refresh(true)" />
          <SearchTextInput :value.sync="search.name" :placeholder="$t('member.tab.ticketBoxHistory.searchNamePlaceholder')" @refresh="refresh(true)" />
        </div>
        <div class="filter-row">
          <BaseElSelect
            v-model="search.status"
            clearable
            :placeholder="$t('member.tab.ticketBoxHistory.usageStatus')"
            @change="refresh(true)"
          >
            <BaseElSelectOption
              v-for="option in statusOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </BaseElSelect>
          <BaseElSelect
            v-model="search.sort"
            :placeholder="$t('member.tab.ticketBoxHistory.sortBy')"
            @change="refresh(true)"
          >
            <BaseElSelectOption
              v-for="option in sortOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </BaseElSelect>
        </div>
      </div>

      <BaseTable v-loading="loading.table" :data="tableData">
        <EmptyBlock slot="empty" />
        <BaseElTableColumn prop="code" :label="$t('member.tab.ticketBoxHistory.code')" min-width="140" align="center" />
        <BaseElTableColumn prop="name" :label="$t('member.tab.ticketBoxHistory.name2')" min-width="140" align="center" />
        <BaseElTableColumn :label="$t('member.tab.ticketBoxHistory.origin')" min-width="130" align="center">
          <template slot-scope="scope">
            {{ originLabel(scope.row.origin) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="$t('member.tab.ticketBoxHistory.statusCol')" min-width="100" align="center">
          <template slot-scope="scope">
            <Tag :type="statusTagType(scope.row.status)">
              {{ statusLabelText(scope.row.status) }}
            </Tag>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn prop="availableUseTimes" :label="$t('member.tab.ticketBoxHistory.remainingRedeem')" min-width="120" align="center" />
        <BaseElTableColumn :label="$t('member.tab.ticketBoxHistory.createdAt')" min-width="160" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="$t('member.tab.ticketBoxHistory.expiryDate')" min-width="160" align="center">
          <template slot-scope="scope">
            {{ scope.row.exp ? formatDateTime(scope.row.exp) : '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="$t('member.tab.ticketBoxHistory.action')" fixed="right" width="200" align="center">
          <template slot-scope="scope">
            <div class="flex items-center justify-center gap-[8px]">
              <BaseElButton type="text" @click="openDetail(scope.row)">{{ $t('member.tab.ticketBoxHistory.detail') }}</BaseElButton>
              <BaseElButton
                type="text"
                :disabled="!canRedeem(scope.row)"
                @click="openRedeem(scope.row)"
              >
                {{ $t('member.tab.ticketBoxHistory.redeem') }}
              </BaseElButton>
              <BaseElButton
                type="text"
                class="text-danger"
                :disabled="!canVoid(scope.row)"
                @click="openVoid(scope.row)"
              >
                {{ $t('member.tab.ticketBoxHistory.void') }}
              </BaseElButton>
            </div>
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="tableDataCount"
        @pageChange="refresh(false)"
      />
    </section>

    <!-- 詳情 / 核銷 / 作廢 -->
    <TicketBoxDetailDrawer
      v-if="drawer.detail"
      :recordId="selectedRow.id"
      @close="drawer.detail = false"
      @void="onVoidFromDrawer"
      @refresh="refresh(false)"
    />
    <TicketBoxRedeemDialog
      v-if="dialog.redeem"
      :recordId="selectedRow.id"
      @close="dialog.redeem = false"
      @confirm="onActionDone"
    />
    <TicketBoxVoidDialog
      v-if="dialog.void"
      :recordId="selectedRow.id"
      @close="dialog.void = false"
      @confirm="onActionDone"
    />
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import { useShop } from '@/use/shop'
import { useTable } from '@/use/table'
import { noEmptyRules } from '@/validation/index'
import formUtils from '@/utils/form'
import { formatDate } from '@/utils/date'
import { find } from 'lodash'
import SearchTextInput from '@/components/Input/SearchTextInput.vue'
import Tag from '@/components/Tag/Tag.vue'
import BaseElInputNumber from '@/components/BaseElInputNumber.vue'
import TicketBoxSelectModal from './TicketBoxSelectModal.vue'
import TicketBoxDetailDrawer from '@/views/TicketBox/TicketBoxUsageRecord/components/TicketBoxDetailDrawer.vue'
import TicketBoxRedeemDialog from '@/views/TicketBox/TicketBoxUsageRecord/components/TicketBoxRedeemDialog.vue'
import TicketBoxVoidDialog from '@/views/TicketBox/TicketBoxUsageRecord/components/TicketBoxVoidDialog.vue'
import { GetTicketBox } from '@/api/ticketBox'
import {
  CreateTicketBoxRecord,
  GetTicketBoxRecord,
  GetTicketBoxRecordCount,
} from '@/api/ticketBoxRecord'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'TicketBoxHistoryTab',
  components: {
    SearchTextInput,
    Tag,
    BaseElInputNumber,
    TicketBoxSelectModal,
    TicketBoxDetailDrawer,
    TicketBoxRedeemDialog,
    TicketBoxVoidDialog,
  },
  props: {
    memberId: { type: String, required: true },
  },
  setup (props) {
    const { shopId } = useShop()
    const {
      tableData,
      tableOptions,
      tableDataCount,
      loading: tableLoading,
      pageStartIndex,
    } = useTable()

    const addFormRef = ref(null)
    const showTicketBoxSelectModal = ref(false)
    const ticketBoxList = ref([])
    const selectedRow = reactive({ id: null })
    const drawer = reactive({ detail: false })
    const dialog = reactive({ redeem: false, void: false })

    const loading = reactive({
      add: false,
      table: false,
    })

    const search = reactive({
      code: null,
      name: null,
      status: null,
      sort: 'createdAt',
    })

    const formData = reactive({
      ticketBoxId: null,
      availableTimes: 1,
    })

    const formRules = {
      ticketBoxId: [noEmptyRules()],
    }

    const statusOptions = [
      { label: i18n.t('member.tab.ticketBoxHistory.statusOpen'), value: 'all' },
      { label: i18n.t('member.tab.ticketBoxHistory.statusOpen'), value: 'open' },
      { label: i18n.t('member.tab.ticketBoxHistory.statusAvailable'), value: 'available' },
      { label: i18n.t('member.tab.ticketBoxHistory.statusComplete'), value: 'complete' },
      { label: i18n.t('member.tab.ticketBoxHistory.statusOverdue'), value: 'overdue' },
      { label: i18n.t('member.tab.ticketBoxHistory.statusVoided'), value: 'voided' },
    ]

    const sortOptions = [
      { label: i18n.t('member.tab.ticketBoxHistory.sortCreatedAt'), value: 'createdAt' },
      { label: i18n.t('member.tab.ticketBoxHistory.sortExpiry'), value: 'exp' },
    ]

    const selectedTicketBoxName = computed(() => {
      const item = find(ticketBoxList.value, { id: formData.ticketBoxId })
      return item ? item.name : ''
    })

    const originLabel = (origin) => {
      const map = { eventPlaybook: i18n.t('member.tab.ticketBoxHistory.originEventPlaybook'), admin: i18n.t('member.tab.ticketBoxHistory.originAdmin'), system: i18n.t('member.tab.ticketBoxHistory.originSystem') }
      return map[origin] || '-'
    }

    const statusLabelText = (status) => {
      const map = { open: i18n.t('member.tab.ticketBoxHistory.statusOpen'), available: i18n.t('member.tab.ticketBoxHistory.statusAvailable'), complete: i18n.t('member.tab.ticketBoxHistory.statusComplete'), overdue: i18n.t('member.tab.ticketBoxHistory.statusOverdue'), voided: i18n.t('member.tab.ticketBoxHistory.statusVoided') }
      return map[status] || '-'
    }

    const statusTagType = (status) => {
      const map = { open: 'info', available: 'action', complete: 'success', overdue: 'warning', voided: 'danger' }
      return map[status] || 'info'
    }

    const canRedeem = (row) => ['open', 'available'].includes(row.status) && row.availableUseTimes > 0
    const canVoid = (row) => ['open', 'available'].includes(row.status)
    const formatDateTime = (time) => time ? formatDate(time, 'YYYY/MM/DD HH:mm') : '-'

    const fetchTicketBoxList = async () => {
      const [res, err] = await GetTicketBox({
        shopId: shopId.value,
        start: 0,
        limit: 999,
      })
      if (err) return
      ticketBoxList.value = res || []
    }

    const getList = async () => {
      const [res, err] = await GetTicketBoxRecord({
        shopId: shopId.value,
        MemberId: props.memberId,
        code: search.code || undefined,
        name: search.name || undefined,
        status: search.status || undefined,
        sort: search.sort || undefined,
        start: pageStartIndex.value,
        limit: tableOptions.pageLimit,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      tableData.value = res
    }

    const getCount = async () => {
      const [res, err] = await GetTicketBoxRecordCount({
        shopId: shopId.value,
        MemberId: props.memberId,
        code: search.code || undefined,
        name: search.name || undefined,
        status: search.status || undefined,
      })
      if (err) return
      tableDataCount.value = res.count
    }

    const refresh = async (resetPage) => {
      if (resetPage) tableOptions.page = 1
      loading.table = true
      await Promise.allSettled([getList(), getCount()])
      loading.table = false
    }

    const clearForm = () => {
      formData.ticketBoxId = null
      formData.availableTimes = 1
    }

    const onSelectTicketBox = (id) => {
      formData.ticketBoxId = id
      showTicketBoxSelectModal.value = false
    }

    const onCreateRecord = async () => {
      if (!(await formUtils.checkForm(addFormRef.value))) return
      if (formData.availableTimes < 1) {
        window.$message.error(i18n.t('member.tab.ticketBoxHistory.quantityMustBePositive'))
        return
      }
      loading.add = true
      const [, err] = await CreateTicketBoxRecord({
        shopId: shopId.value,
        memberId: props.memberId,
        ticketBoxId: formData.ticketBoxId,
        availableTimes: formData.availableTimes,
      })
      loading.add = false
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success(i18n.t('member.tab.ticketBoxHistory.addSuccess'))
      clearForm()
      refresh(false)
    }

    const openDetail = (row) => {
      selectedRow.id = row.id
      drawer.detail = true
    }
    const openRedeem = (row) => {
      selectedRow.id = row.id
      dialog.redeem = true
    }
    const openVoid = (row) => {
      selectedRow.id = row.id
      dialog.void = true
    }

    const onActionDone = () => {
      dialog.redeem = false
      dialog.void = false
      refresh(false)
    }

    const onVoidFromDrawer = () => {
      drawer.detail = false
      refresh(false)
    }

    onMounted(() => {
      fetchTicketBoxList()
      refresh(false)
    })

    return {
      addFormRef,
      formData,
      formRules,
      showTicketBoxSelectModal,
      ticketBoxList,
      selectedTicketBoxName,
      search,
      statusOptions,
      sortOptions,
      tableData,
      tableOptions,
      tableDataCount,
      loading,
      selectedRow,
      drawer,
      dialog,
      originLabel,
      statusLabelText,
      statusTagType,
      canRedeem,
      canVoid,
      formatDateTime,
      refresh,
      clearForm,
      onSelectTicketBox,
      onCreateRecord,
      openDetail,
      openRedeem,
      openVoid,
      onActionDone,
      onVoidFromDrawer,
    }
  },
})
</script>

<style lang="postcss" scoped>
.filters-wrapper {
  @apply flex flex-col gap-[8px];
}
.filter-row {
  @apply flex items-center gap-[8px];
}
::v-deep .quantity-input.el-input-number {
  width: 560px;
}
::v-deep .quantity-input.el-input-number input {
  width: 560px;
}
</style>
